import { AxiosPromise } from "axios";
import configs from "../configs";
import { AuthLiveApis } from "./live/authLiveApis";


// Response type definitions
export interface ApiResponse<T = any> {
  success: boolean;
  data: T;
  message: string;
}

export interface UserData {
  token: string;
  user_id?: string;
  id?: string;
  name?: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  email: string;
  phone_number?: string;
  kyc_status?: string;
  account_id?: string;
  [key: string]: any;
}

export interface KycStatusData {
  profile_completed: boolean;
  verified_general: boolean;
  foreign_accounts_setup: boolean;
  status: string;
  has_profile_picture: boolean;
  kyc_status?: string;
}

export interface KycNextStepData {
  next_step: string;
  status: string;
}

/**
 * Authentication API service
 */
export class AuthApis {
    private static authLiveApis: AuthLiveApis = new AuthLiveApis();

    /**
     * Register a new user
     * @param data Registration form data
     */
    static register(data: FormData): AxiosPromise<ApiResponse<UserData>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.register(data);
        }
    }  

    /**
     * Verify OTP code
     * @param data OTP verification data
     */
    static verifyOtp(data: { user_id: string; otp: string }): AxiosPromise<ApiResponse<UserData>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.verifyOtp(data);
        }
    } 
 
    /**
     * Resend OTP code
     * @param data User ID for resending OTP
     */
    static resendOtp(data: { user_id: string }): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.resendOtp(data);
        }
    } 
   
    /**
     * Setup user profile
     * @param data Profile setup data
     */
    static setUpProfile(data: FormData): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.setUpProfile(data);
        }
    } 

    /**
     * Complete user profile with KYC data
     * @param data KYC profile completion data
     */
    static completeProfile(data: any): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.completeProfile(data);
        }
    } 
    
    /**
     * Save purposes for using the platform
     * @param data User purposes
     */
    static savePurposes(data: { user_id: string; purpose_for_using: string }): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.savePurposes(data);
        }
    } 

    /**
     * User login
     * @param data Login credentials
     */
    static login(data: { email: string; password: string }): AxiosPromise<ApiResponse<UserData>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.login(data);
        }
    } 

    /**
     * Get user KYC status
     */
    static getKycStatus(token:any): AxiosPromise<Array<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.getKycStatus(token);
        }
    }

    /**
     * Get next KYC step for the user
     */
    static getKycNextStep(): AxiosPromise<ApiResponse<KycNextStepData>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.getKycNextStep();
        }
    }
    
    /**
     * Create user survey
     * @param data Survey data
     */
    static createSurvey(data: any): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.createSurvey(data);
        }
    }
    
    /**
     * Request password reset
     * @param data User email
     */
    static forgotPassword(data: FormData): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.forgotPassword(data);
        }
    }
   
    /**
     * Alternative password reset method
     * @param data User email
     */
    static forgotPWord(data: FormData): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.forgotPWord(data);
        }
    }

   /**
 * Reset password with token
 * @param data Password reset data (either FormData or object)
 * @deprecated Use resetPassword instead for consistency
 */
static resetPWord(data: FormData | { token: string; password: string; password_confirmation: string }): AxiosPromise<ApiResponse<any>> {
    if (configs.type === "LOCAL") {
        return {} as AxiosPromise;
    } else {
        return this.authLiveApis.resetPWord(data);
    }
}

/**
 * Reset password with token
 * @param data Password reset data (either FormData or object)
 */
static resetPassword(data: FormData | { token: string; password: string; password_confirmation: string }): AxiosPromise<ApiResponse<any>> {
    if (configs.type === "LOCAL") {
        return {} as AxiosPromise;
    } else {
        return this.authLiveApis.resetPassword(data);
    }
}

    /**
     * Verify user email
     * @param data Email verification data
     */
    static verifyMail(data: any): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.verifyMail(data);
        }
    }

    /**
     * User logout
     * @param data Logout data
     */
    static logout(data: any): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.logout(data);
        }
    }
    
    static saveWaitList(data: any): AxiosPromise<ApiResponse<any>> {
        if (configs.type === "LOCAL") {
            return {} as AxiosPromise;
        } else {
            return this.authLiveApis.saveWaitList(data);
        }
    }

}